import { Box, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TableSearch from '../../../components/Table/TableSearch';
import { ACCOUNTS_PAGE_FILTERS } from '../accountsPageFilters';
import TableFiltersItems from '../../../components/Table/TableRenderFiltersItems';
import { FilterItem } from '../../../interfaces/filterOptions.interface';
import { ActiveFilters } from '../../../hooks/useTablePageParams';
import MultiSelect from '../../../components/MultiSelect';
import STRING_KEYS from '../../../language/keys';
import { AccountStatusEnum } from '../../../consts/enums';
import { ACCOUNT_KEY_SHORTCUTS_PARAMS } from '../../../consts/requestParamsKeys';
import { useGetAccountPlanDefinitions } from '../../../hooks/query/useAccounts';
import style from './style';

type AccountsFilterProps = {
  activeFilters: ActiveFilters;
  onChangeFilter: (filterName: string, newValue: string[]) => void;
};

const AccountsFilters = ({
  activeFilters,
  onChangeFilter,
}: AccountsFilterProps): JSX.Element => {
  const { t: translate } = useTranslation();

  const { data: planDefinitions = [] } = useGetAccountPlanDefinitions();

  const handleSearch = (searchValue: string): void => {
    if (searchValue) {
      const searchParts = searchValue.split(':');
      if (searchParts.length > 1) {
        const key = searchParts[0];
        const val = searchParts[1];
        if (key.toLowerCase() === ACCOUNT_KEY_SHORTCUTS_PARAMS.SUBDOMAIN) {
          onChangeFilter(ACCOUNTS_PAGE_FILTERS.ACCOUNT_SUBDOMAIN, [val]);

          return;
        }
      }
    }
    onChangeFilter(ACCOUNTS_PAGE_FILTERS.QUERY, [searchValue]);
  };

  const handleDelete = (filterItem: FilterItem) => {
    switch (filterItem.field) {
      case ACCOUNTS_PAGE_FILTERS.QUERY: {
        handleSearch('');
        break;
      }
      case ACCOUNTS_PAGE_FILTERS.ACCOUNT_STATUS: {
        const filteredStatuses = activeFilters[
          ACCOUNTS_PAGE_FILTERS.ACCOUNT_STATUS
        ].filter((filter: string) => {
          return filter === filterItem.field;
        });

        onChangeFilter(ACCOUNTS_PAGE_FILTERS.ACCOUNT_STATUS, filteredStatuses);
        break;
      }
      case ACCOUNTS_PAGE_FILTERS.ACCOUNT_SUBDOMAIN: {
        const filteredStatuses = activeFilters[
          ACCOUNTS_PAGE_FILTERS.ACCOUNT_SUBDOMAIN
        ].filter((filter: string) => {
          return filter === filterItem.field;
        });

        onChangeFilter(
          ACCOUNTS_PAGE_FILTERS.ACCOUNT_SUBDOMAIN,
          filteredStatuses,
        );
        break;
      }
      case ACCOUNTS_PAGE_FILTERS.ACCOUNT_PRICING_PLAN: {
        const filteredStatuses = activeFilters[
          ACCOUNTS_PAGE_FILTERS.ACCOUNT_PRICING_PLAN
        ].filter((filter: string) => {
          return filter === filterItem.field;
        });

        onChangeFilter(
          ACCOUNTS_PAGE_FILTERS.ACCOUNT_PRICING_PLAN,
          filteredStatuses,
        );
        break;
      }
      default:
    }
  };

  const createActiveFilterObject = (
    fieldName: string,
    label: string,
    value: string,
    displayValue?: string,
  ) => {
    return {
      label,
      field: fieldName,
      value,
      displayValue: displayValue || value,
    };
  };

  const getActiveFilters = (): FilterItem[] => {
    const filters: FilterItem[] = [];

    if (activeFilters[ACCOUNTS_PAGE_FILTERS.QUERY]) {
      activeFilters[ACCOUNTS_PAGE_FILTERS.QUERY].forEach((item) => {
        if (item.length > 0) {
          filters.push(
            createActiveFilterObject(
              ACCOUNTS_PAGE_FILTERS.QUERY,
              'Search',
              item,
            ),
          );
        }
      });
    }

    if (activeFilters[ACCOUNTS_PAGE_FILTERS.ACCOUNT_SUBDOMAIN]) {
      activeFilters[ACCOUNTS_PAGE_FILTERS.ACCOUNT_SUBDOMAIN].forEach((item) => {
        if (item.length > 0) {
          filters.push(
            createActiveFilterObject(
              ACCOUNTS_PAGE_FILTERS.ACCOUNT_SUBDOMAIN,
              translate(STRING_KEYS.ACCOUNTS_SUBDOMAIN),
              item,
            ),
          );
        }
      });
    }

    if (activeFilters[ACCOUNTS_PAGE_FILTERS.ACCOUNT_STATUS]) {
      activeFilters[ACCOUNTS_PAGE_FILTERS.ACCOUNT_STATUS].forEach((item) => {
        if (item.length > 0) {
          filters.push(
            createActiveFilterObject(
              ACCOUNTS_PAGE_FILTERS.ACCOUNT_STATUS,
              translate(STRING_KEYS.STATUS),
              item,
              translate(STRING_KEYS.ACCOUNT_STATUSES[item]),
            ),
          );
        }
      });
    }

    if (activeFilters[ACCOUNTS_PAGE_FILTERS.ACCOUNT_PRICING_PLAN]) {
      activeFilters[ACCOUNTS_PAGE_FILTERS.ACCOUNT_PRICING_PLAN].forEach(
        (item) => {
          if (item.length > 0) {
            const planDef = planDefinitions.find(({ pricingPlan }) => pricingPlan === item);

            filters.push(
              createActiveFilterObject(
                ACCOUNTS_PAGE_FILTERS.ACCOUNT_PRICING_PLAN,
                translate(STRING_KEYS.POSITIONS_LIST_FILTER_PRICING_PLAN),
                item,
                planDef?.displayName,
              ),
            );
          }
        },
      );
    }

    return filters;
  };

  const handleChangeStatus = (status: string[]): void => {
    onChangeFilter(ACCOUNTS_PAGE_FILTERS.ACCOUNT_STATUS, status);
  };

  const handlePricingPlanChanged = (pricingPlan: string[]): void => {
    onChangeFilter(ACCOUNTS_PAGE_FILTERS.ACCOUNT_PRICING_PLAN, pricingPlan);
  };

  const statusesOptions = [
    {
      label: translate(
        STRING_KEYS.ACCOUNT_STATUSES[AccountStatusEnum.onboarding],
      ),
      value: AccountStatusEnum.onboarding,
    },
    {
      label: translate(STRING_KEYS.ACCOUNT_STATUSES[AccountStatusEnum.active]),
      value: AccountStatusEnum.active,
    },
    {
      label: translate(
        STRING_KEYS.ACCOUNT_STATUSES[AccountStatusEnum.suspended],
      ),
      value: AccountStatusEnum.suspended,
    },
    {
      label: translate(
        STRING_KEYS.ACCOUNT_STATUSES[AccountStatusEnum.deactivated],
      ),
      value: AccountStatusEnum.deactivated,
    },
  ];

  const pricingPlanOptions =
      planDefinitions.map(({ displayName, pricingPlan }) => ({
        label: displayName,
        value: pricingPlan,
      }));

  return (
    <>
      <TableSearch handleSearch={ handleSearch } />
      <Divider />
      <Box sx={ style.BoxFilters.sx }>
        <MultiSelect
          label={ translate(STRING_KEYS.STATUS) }
          onChange={ handleChangeStatus }
          options={ statusesOptions }
          value={ activeFilters[ACCOUNTS_PAGE_FILTERS.ACCOUNT_STATUS] }
        />
        <MultiSelect
          label={ translate(STRING_KEYS.POSITIONS_LIST_FILTER_PRICING_PLAN) }
          onChange={ handlePricingPlanChanged }
          options={ pricingPlanOptions }
          value={ activeFilters[ACCOUNTS_PAGE_FILTERS.ACCOUNT_PRICING_PLAN] }
        />
      </Box>
      <Divider />
      <TableFiltersItems filters={ getActiveFilters() } onDelete={ handleDelete } />
    </>
  );
};

export default AccountsFilters;
